import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Users } from "lucide-react";

export const AttendeeDialog = ({ open, onOpenChange, program, adultName, childrenList = [], initialRefs, busy, onConfirm, onCancel }) => {
  const audience = program?.audience || "together";
  const allowAdult = audience !== "children";
  const allowChildren = audience !== "adult";
  const [refs, setRefs] = useState([]);
  const needsAdult = audience === "together" && refs.some((r) => r !== "adult") && !refs.includes("adult");
  const needsChild = audience === "together" && refs.includes("adult") && !refs.some((r) => r !== "adult");

  useEffect(() => {
    if (!open) return;
    const allowedInit = (initialRefs || []).filter((r) => (r === "adult" ? allowAdult : allowChildren));
    if (allowedInit.length) { setRefs(allowedInit); return; }
    if (allowAdult) setRefs(["adult"]);
    else if (allowChildren && childrenList.length) setRefs([childrenList[0].child_id]);
    else setRefs([]);
    // Only re-initialise when the dialog OPENS, not on every re-render
    // (initialRefs is a fresh array each render; depending on it would reset
    //  the user's ticks during the "Mentés…" busy re-render).
  }, [open]); // eslint-disable-line

  const toggle = (r) => setRefs((s) => (s.includes(r) ? s.filter((x) => x !== r) : [...s, r]));

  const Row = ({ id, label, sub }) => (
    <label data-testid={`attendee-opt-${id}`}
      className={`flex items-center gap-3 rounded-xl border p-3 cursor-pointer transition-colors ${refs.includes(id) ? "border-poppy bg-poppy/5" : "border-stone-200 hover:border-poppy/40"}`}>
      <input type="checkbox" checked={refs.includes(id)} onChange={() => toggle(id)} data-testid={`attendee-checkbox-${id}`} className="accent-poppy w-4 h-4" />
      <span className="text-sm text-ink font-medium">{label}{sub && <span className="text-inkmuted font-normal"> · {sub}</span>}</span>
    </label>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-sm" data-testid="attendee-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl font-normal flex items-center gap-2">
            <Users size={20} strokeWidth={1.6} className="text-poppy" /> Kinek foglalsz helyet?
          </DialogTitle>
        </DialogHeader>
        {program && <p className="text-sm text-inkmuted -mt-2">{program.title} · {program.start_date} {program.start_time}</p>}
        <div className="space-y-2 mt-2">
          {allowAdult && <Row id="adult" label={adultName || "Felnőtt"} sub="felnőtt" />}
          {allowChildren && childrenList.map((c) => <Row key={c.child_id} id={c.child_id} label={c.name} sub={`${c.age} éves`} />)}
        </div>
        {audience === "adult" && (
          <p className="text-xs text-inkmuted" data-testid="attendee-audience-note">Ez egy felnőtt program – csak a saját nevedre foglalhatsz.</p>
        )}
        {audience === "children" && childrenList.length === 0 && (
          <p className="text-xs text-amber-600" data-testid="attendee-audience-note">Ez egy gyermekprogram – előbb adj hozzá gyermeket a Profil → Családom fülön.</p>
        )}
        {audience === "children" && childrenList.length > 0 && (
          <p className="text-xs text-inkmuted" data-testid="attendee-audience-note">Ez egy gyermekprogram – válaszd ki, melyik gyermekednek foglalsz.</p>
        )}
        {audience === "together" && childrenList.length === 0 && (
          <p className="text-xs text-amber-600" data-testid="attendee-add-child-note">
            Előbb add hozzá a gyermeked a profilodhoz:{" "}
            <Link
              to="/profil?tab=family"
              onClick={() => onOpenChange?.(false)}
              data-testid="attendee-add-child-link"
              className="font-medium underline text-poppy hover:text-poppy/80 transition-colors"
            >
              Fiók → Családom
            </Link>
          </p>
        )}
        {needsAdult && (
          <p className="text-xs text-amber-600" data-testid="attendee-need-adult-note">Közös program – gyermek mellé pipáld be a felnőttet is.</p>
        )}
        {needsChild && (
          <p className="text-xs text-amber-600" data-testid="attendee-need-child-note">Közös program – felnőtt mellé pipálj be legalább egy gyermeket is.</p>
        )}
        <button onClick={() => onConfirm(refs)} disabled={busy || refs.length === 0 || needsAdult || needsChild} data-testid="attendee-confirm-btn"
          className="mt-3 w-full rounded-full bg-poppy text-white py-3 text-sm hover:bg-poppy/90 transition-colors disabled:opacity-50">
          {busy ? "Mentés…" : `Foglalás megerősítése (${refs.length} fő)`}
        </button>
        {onCancel && (
          <button onClick={onCancel} disabled={busy} data-testid="attendee-cancel-btn"
            className="mt-2 w-full rounded-full border border-red-300 text-red-500 py-2.5 text-sm hover:bg-red-50 transition-colors disabled:opacity-50">
            Foglalás lemondása
          </button>
        )}
      </DialogContent>
    </Dialog>
  );
};
